import React, { useCallback, useEffect } from 'react';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import './SearchSuggestions.scss';

import {
    LOANFORMAT_EAUDIOBOOK_OBJECT,
    LOANFORMAT_EBOOK_OBJECT,
    LOANFORMAT_EPRESS_OBJECT
} from '../../store/loanFormat/actions';
import ProductList from '../common/ProductList';
import Utils from '../utils';
import * as types from '../../store/constants';
import RoutingUtils from '../utils/utils-routing';
import {
    mapLanguageScopeKeyToCamelCase,
    mapLanguageScopeKeyToName,
    SCOPE_EAUDIOBOOK_OBJECT,
    SCOPE_EBOOK_OBJECT,
    SCOPE_EPRESS_OBJECT,
    SCOPE_LANGUAGELEARNING_OBJECT,
    SCOPE_LOTE_OBJECT
} from '../../store/search/actions';

import SearchCatalogueListItem from './SearchCatalogueListItem';
import { TopHit } from './TopHit';
import { useHandleArrowKeySuggestionNavigation } from './useHandleArrowKeySuggestionNavigation';
import { DISPLAY_TYPE_LIST } from '../../store/displayType/types';
import { SearchScope } from '../../store/search/state';
import { ProductSearchResponseOut, TopHitOut } from '../../store/api';
import { OrderListState } from '../../store/orderlist/state';
import { Orderlist } from '../../../models/OrderList';
import { useTranslation } from 'react-i18next';
import { ProductSuggestion } from './ProductSuggestion';
import { SingleAddOptions } from '../../../models/ListContentCheckOptions';

type Props = {
    readonly orderlist: OrderListState;
    readonly orderlists: ReadonlyArray<Orderlist>;
    readonly searchScope: Partial<SearchScope> | undefined;
    readonly searchSuggestions?: { [key: string]: ProductSearchResponseOut };
    readonly searchSuggestionTopHits: ReadonlyArray<TopHitOut> | undefined;
    readonly searchQuery: string;
    readonly hideSuggestions?: boolean;
    readonly closeSuggestions: () => void;
    readonly onShowAllOfLoanformat: (scopeKey: string) => void;
    readonly onShowAllOfScope: (scope: SearchScope) => void;
    readonly deactivateSearchForm: () => void;
    readonly scrollInnerRef: React.RefObject<HTMLDivElement>;
    readonly onAddPrice?: (options: SingleAddOptions) => Promise<void>;
};

interface LanguageProductSuggestionsProps {
    searchSuggestions: { [key: string]: ProductSearchResponseOut } | undefined;
    scopeFormatKey: SearchScope;
}

export const SearchSuggestions = (props: Props) => {
    const handleArrowKeySuggestionNavigation = useHandleArrowKeySuggestionNavigation();
    const { t } = useTranslation();

    const getSearchTerms = useCallback(() => {
        const searchTerm = props.searchQuery;
        return searchTerm.split(' ').map(term => term.replace(/[^a-zA-Z\d ]/g, ''));
    }, [props.searchQuery]);

    useEffect(() => {
        if (props.hideSuggestions) {
            window.removeEventListener('keydown', handleArrowKeySuggestionNavigation);
        }

        window.addEventListener('keydown', handleArrowKeySuggestionNavigation);

        return () => {
            window.removeEventListener('keydown', handleArrowKeySuggestionNavigation);
        };
    }, [handleArrowKeySuggestionNavigation, props.hideSuggestions]);

    const AuthorTopHit = () => {
        const author = props.searchSuggestionTopHits?.find(topHit => topHit.tag === 'authors')?.object;
        const authorFilter = { author: { include: [author?.id] } };
        const authorName = author ? Utils.getPerformerFullname(author) : '';
        return (
            <>
                {author ? (
                    <TopHit
                        label={t('components.searchResults.refinedSearchCategories.author')}
                        photoUrl={Utils.performerPhotoUrl(author?.id)}
                        onClick={props.closeSuggestions}
                        linkTo={Utils.topHitLocationDescriptor(
                            authorFilter,
                            props.searchScope?.key,
                            authorName,
                            '/search'
                        )}
                        highlightSearchWords={getSearchTerms()}
                        textToHighlight={authorName!}
                    />
                ) : null}
            </>
        );
    };

    const NarratorTopHit = () => {
        const narrator = props.searchSuggestionTopHits?.find(topHit => topHit.tag === 'narrators')?.object;
        const narratorFilter = { narrator: { include: [narrator?.id] } };
        const narratorName = narrator ? Utils.getPerformerFullname(narrator) : '';

        return (
            <>
                {narrator ? (
                    <TopHit
                        label={t('components.searchResults.refinedSearchCategories.narrator')}
                        photoUrl={Utils.performerPhotoUrl(narrator?.id)}
                        onClick={props.closeSuggestions}
                        linkTo={Utils.topHitLocationDescriptor(
                            narratorFilter,
                            props.searchScope?.key,
                            narratorName,
                            '/search'
                        )}
                        highlightSearchWords={getSearchTerms()}
                        textToHighlight={narratorName!}
                    />
                ) : null}
            </>
        );
    };

    const SeriesTopHit = () => {
        const series = props.searchSuggestionTopHits?.find(topHit => topHit.tag === 'seriesName')?.object;
        const seriesFilter = { series: { include: [series] } };
        return (
            <>
                {series ? (
                    <TopHit
                        label={t('components.searchResults.refinedSearchCategories.series')}
                        onClick={props.closeSuggestions}
                        linkTo={Utils.topHitLocationDescriptor(seriesFilter, props.searchScope?.key, series, '/search')}
                        highlightSearchWords={getSearchTerms()}
                        textToHighlight={series}
                    />
                ) : null}
            </>
        );
    };

    const GenreTopHit = () => {
        if (props.searchScope?.key === 'catalogue') {
            return null; // Genres page is always scoped with ebook or eaudiobooks
        }

        const genre = props.searchSuggestionTopHits?.find(topHit => topHit.tag === 'genres')?.object;
        const genreFilter = { genre: { include: [genre?.id] } };
        const genreLocation = props.searchScope?.key
            ? RoutingUtils.genrePageLocationDescriptor(props.searchScope.key, genre?.id)
            : Utils.topHitLocationDescriptor(genreFilter, null, genre?.name, '/search');

        return (
            <>
                {genre ? (
                    <TopHit
                        label={t('components.searchResults.refinedSearchCategories.genre')}
                        linkTo={genreLocation}
                        onClick={props.closeSuggestions}
                        highlightSearchWords={getSearchTerms()}
                        textToHighlight={genre.name}
                    />
                ) : null}
            </>
        );
    };

    const BundleTopHit = () => {
        const bundle = props.searchSuggestionTopHits?.find(topHit => topHit.tag === 'bundle')?.object;

        return (
            <>
                {bundle ? (
                    <TopHit
                        label={t('components.searchSuggestions.curatedCollection')}
                        linkTo={`${types.PATH_BUNDLES}/${bundle.bundleId}`}
                        onClick={props.closeSuggestions}
                        photoUrl={bundle.coverUrl}
                        photoClassName="bundle"
                        highlightSearchWords={getSearchTerms()}
                        textToHighlight={bundle.title}
                    />
                ) : null}
            </>
        );
    };

    const PublisherImprintTopHit = () => {
        const publisherImprint = props.searchSuggestionTopHits?.find(
            topHit => topHit.tag === 'publisherImprint'
        )?.object;
        const publisherImprintFilter = {
            publisherImprint: { include: [publisherImprint] }
        };
        return (
            <>
                {publisherImprint ? (
                    <TopHit
                        label={t('components.searchResults.refinedSearchCategories.publisherImprint')}
                        linkTo={Utils.topHitLocationDescriptor(
                            publisherImprintFilter,
                            props.searchScope?.key,
                            publisherImprint,
                            '/search'
                        )}
                        onClick={props.closeSuggestions}
                        highlightSearchWords={getSearchTerms()}
                        textToHighlight={publisherImprint}
                    />
                ) : null}
            </>
        );
    };

    const PublisherTopHit = () => {
        if (props.searchScope?.key?.toLowerCase() === 'catalogue') {
            return null; // Publisher page is always scoped with ebook or eaudiobooks
        }

        const publisher = props.searchSuggestionTopHits?.find(topHit => topHit.tag === 'publisher')?.object;
        const publisherFilter = { publisher: { include: [publisher] } };
        const publisherLocation = props.searchScope?.key
            ? RoutingUtils.publisherPageLocationDescriptor(props.searchScope.key, publisher)
            : Utils.topHitLocationDescriptor(publisherFilter, null, publisher, '/search');
        return (
            <>
                {publisher ? (
                    <TopHit
                        label={t('components.searchResults.refinedSearchCategories.publisher')}
                        linkTo={publisherLocation}
                        onClick={props.closeSuggestions}
                        highlightSearchWords={getSearchTerms()}
                        textToHighlight={publisher}
                    />
                ) : null}
            </>
        );
    };

    function LanguageProductSuggestions({ searchSuggestions, scopeFormatKey }: LanguageProductSuggestionsProps) {
        const scopeName = mapLanguageScopeKeyToName(scopeFormatKey);
        const key = mapLanguageScopeKeyToCamelCase(scopeFormatKey);
        const searchSuggestionHits = searchSuggestions?.[scopeName]?.hits;

        return (
            <>
                {searchSuggestionHits && searchSuggestionHits.length > 0 ? (
                    <div>
                        <h4>
                            {t(`components.searchSuggestions.languageScope.${key}`, { defaultValue: scopeName })}
                            <a className="more" onClick={() => props.onShowAllOfScope(scopeFormatKey)}>
                                {t('components.searchSuggestions.seeAll', {
                                    totalHits: searchSuggestions[scopeName].totalHits,
                                    label: t(`components.searchSuggestions.languageScope.${key}`, {
                                        defaultValue: scopeName
                                    })
                                })}
                                <i className="material-icons">keyboard_arrow_right</i>
                            </a>
                        </h4>
                        <ProductList
                            closeSuggestions={props.closeSuggestions}
                            onAddPrice={props.onAddPrice}
                            products={searchSuggestionHits}
                            displayType={DISPLAY_TYPE_LIST}
                            isProductSelectable={false}
                            orderlist={props.orderlist}
                            orderlists={props.orderlists}
                            listItemComponent={SearchCatalogueListItem}
                            searchTerms={getSearchTerms()}
                            loanFormat={scopeFormatKey}
                        />
                    </div>
                ) : null}
            </>
        );
    }

    if (props.hideSuggestions) {
        return null;
    }

    const searchSuggestionTopHits = props.searchSuggestionTopHits;
    const suggestions = Object(props.searchSuggestions);
    const topHitClasses = classNames('top-hit', {
        'no-border': suggestions.length === 0 || searchSuggestionTopHits?.length === 0
    });
    const maxHeight = document.body.getBoundingClientRect().height - 140;

    return (
        <>
            {suggestions.length > 0 || (searchSuggestionTopHits?.length ?? 0) > 0 ? (
                <>
                    <div className="search-suggestions-overlay" onClick={props.closeSuggestions} />
                    <div
                        className="search-suggestions"
                        style={{ maxHeight: `${maxHeight}px` }}
                        ref={props.scrollInnerRef}
                    >
                        <div className={topHitClasses}>
                            {!isEmpty(searchSuggestionTopHits) ? (
                                <>
                                    <h4>{t('components.searchSuggestions.topHits')}</h4>
                                    <AuthorTopHit />
                                    <NarratorTopHit />
                                    <GenreTopHit />
                                    <SeriesTopHit />
                                    <BundleTopHit />
                                    <PublisherTopHit />
                                    <PublisherImprintTopHit />
                                </>
                            ) : null}
                        </div>
                        <div className="suggestion-product-lists">
                            <ProductSuggestion
                                closeSuggestions={props.closeSuggestions}
                                onAddPrice={props.onAddPrice}
                                getSearchTerms={getSearchTerms}
                                onShowAllOfLoanformat={props.onShowAllOfLoanformat}
                                orderlist={props.orderlist}
                                orderlists={props.orderlists}
                                searchSuggestions={props.searchSuggestions}
                                loanFormat={LOANFORMAT_EAUDIOBOOK_OBJECT}
                                scopeKey={SCOPE_EAUDIOBOOK_OBJECT.requestKey}
                            />
                            <ProductSuggestion
                                closeSuggestions={props.closeSuggestions}
                                onAddPrice={props.onAddPrice}
                                getSearchTerms={getSearchTerms}
                                onShowAllOfLoanformat={props.onShowAllOfLoanformat}
                                orderlist={props.orderlist}
                                orderlists={props.orderlists}
                                searchSuggestions={props.searchSuggestions}
                                loanFormat={LOANFORMAT_EBOOK_OBJECT}
                                scopeKey={SCOPE_EBOOK_OBJECT.requestKey}
                            />
                            <ProductSuggestion
                                closeSuggestions={props.closeSuggestions}
                                onAddPrice={props.onAddPrice}
                                getSearchTerms={getSearchTerms}
                                onShowAllOfLoanformat={props.onShowAllOfLoanformat}
                                orderlist={props.orderlist}
                                orderlists={props.orderlists}
                                searchSuggestions={props.searchSuggestions}
                                loanFormat={LOANFORMAT_EPRESS_OBJECT}
                                scopeKey={SCOPE_EPRESS_OBJECT.requestKey}
                            />
                            <LanguageProductSuggestions
                                searchSuggestions={props.searchSuggestions}
                                scopeFormatKey={SCOPE_LOTE_OBJECT.requestKey as unknown as SearchScope}
                            />
                            <LanguageProductSuggestions
                                searchSuggestions={props.searchSuggestions}
                                scopeFormatKey={SCOPE_LANGUAGELEARNING_OBJECT.requestKey as unknown as SearchScope}
                            />
                        </div>
                    </div>
                </>
            ) : null}
        </>
    );
};
